import { createContext, useContext, useEffect, useState, useCallback } from "react";
import fleetApi, { FLEET_TOKEN_KEY } from "@/lib/fleetApi";
import api, { formatApiError } from "@/lib/api";
import { setOrgTimeZone } from "@/lib/time";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [entitlements, setEntitlements] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | authed | anon

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem(FLEET_TOKEN_KEY);
    if (!token) {
      setStatus("anon");
      return;
    }
    try {
      const { data } = await fleetApi.get("/auth/me");
      let timezone = null;
      try {
        const s = await fleetApi.get("/org/settings");
        timezone = s.data?.timezone || null;
      } catch (e) { /* fall back to browser time zone */ }
      setOrgTimeZone(timezone);
      setUser(data);
      setStatus("authed");
      // best-effort org name from dashboard
      try {
        const dash = await fleetApi.get("/dashboard");
        setOrganization({ name: dash.data.org_name, id: data.org_id, timezone });
      } catch (e) {
        setOrganization({ name: data.org_id, id: data.org_id, timezone });
      }
      try {
        const ent = await api.get("/billing/subscription");
        setEntitlements(ent.data.entitlements);
      } catch (e) { /* entitlements optional */ }
    } catch (e) {
      localStorage.removeItem(FLEET_TOKEN_KEY);
      setStatus("anon");
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = async (email, password) => {
    try {
      const { data } = await fleetApi.post("/auth/login", { email, password });
      localStorage.setItem(FLEET_TOKEN_KEY, data.token);
      setUser(data.user);
      setStatus("authed");
      await loadMe();
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    localStorage.removeItem(FLEET_TOKEN_KEY);
    setOrgTimeZone(null);
    setUser(null);
    setOrganization(null);
    setEntitlements(null);
    setStatus("anon");
  };

  return (
    <AuthContext.Provider value={{ user, organization, entitlements, status, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
